<script setup lang="ts">
/**
 * The shared toast surface. One per app, mounted in the layout.
 *
 * `role="status"` with `aria-live="polite"` on the container rather than on each
 * toast: a live region has to exist in the DOM *before* content is inserted into
 * it, or the insertion is not announced. This is the mistake that makes toasts
 * silent for screen-reader users while looking correct on screen.
 *
 * Criticals are `assertive` and do not auto-dismiss — see the ui store.
 */

import MpButton from './MpButton.vue'
import MpIcon from './MpIcon.vue'

import { useUiStore } from '@/stores/ui'

const ui = useUiStore()

const ICONS = { positive: 'check', caution: 'alert', critical: 'alert' } as const
</script>

<template>
  <div class="mp-toaster">
    <div
      class="mp-toaster__region"
      role="status"
      aria-live="polite"
      aria-atomic="false"
    >
      <div
        v-for="toast in ui.toasts.filter(t => t.tone !== 'critical')"
        :key="toast.id"
        class="mp-toast"
        :class="`mp-toast--${toast.tone}`"
      >
        <MpIcon
          :name="ICONS[toast.tone]"
          size="sm"
        />
        <div class="mp-toast__body">
          <p class="mp-toast__message">
            {{ toast.message }}
          </p>
          <p
            v-if="toast.detail"
            class="mp-toast__detail"
          >
            {{ toast.detail }}
          </p>
        </div>
        <MpButton
          variant="ghost"
          size="sm"
          icon="close"
          :icon-only-label="`Dismiss: ${toast.message}`"
          @click="ui.dismiss(toast.id)"
        />
      </div>
    </div>

    <div
      class="mp-toaster__region"
      role="alert"
      aria-live="assertive"
    >
      <div
        v-for="toast in ui.toasts.filter(t => t.tone === 'critical')"
        :key="toast.id"
        class="mp-toast mp-toast--critical"
      >
        <MpIcon
          name="alert"
          size="sm"
        />
        <div class="mp-toast__body">
          <p class="mp-toast__message">
            {{ toast.message }}
          </p>
          <p
            v-if="toast.detail"
            class="mp-toast__detail"
          >
            {{ toast.detail }}
          </p>
        </div>
        <MpButton
          variant="ghost"
          size="sm"
          icon="close"
          :icon-only-label="`Dismiss: ${toast.message}`"
          @click="ui.dismiss(toast.id)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.mp-toaster {
  position: fixed;
  inset-block-end: var(--space-4);
  inset-inline: var(--space-4);
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  pointer-events: none;
}

.mp-toaster__region {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
}

.mp-toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  inline-size: min(100%, 34rem);
  padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
  border: 1px solid var(--border-control);
  border-radius: var(--radius-card);
  background: var(--surface-raised);
  box-shadow: var(--shadow-overlay);
  animation: mp-toast-in var(--motion-base) var(--motion-ease);
}

.mp-toast--positive {
  border-color: var(--positive-text);
  background: var(--positive-bg);
  color: var(--positive-text);
}

.mp-toast--caution {
  border-color: var(--caution-text);
  background: var(--caution-bg);
  color: var(--caution-text);
}

.mp-toast--critical {
  border-color: var(--critical-text);
  background: var(--critical-bg);
  color: var(--critical-text);
}

.mp-toast__body {
  flex: 1;
}

.mp-toast__message {
  font-size: var(--font-size-body);
  font-weight: 600;
}

.mp-toast__detail {
  margin-block-start: var(--space-1);
  font-size: var(--font-size-small);
  font-weight: 400;
}

@keyframes mp-toast-in {
  from {
    opacity: 0;
    translate: 0 var(--space-2);
  }
}

/* Above the 360px bottom tab bar, which owns the bottom of the screen there. */
@media (max-width: 767px) {
  .mp-toaster {
    inset-block-end: calc(var(--target-min) + var(--space-6));
  }
}
</style>
